import React, { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from '../../components/form/AppSelect';
import NumberInput from '../../components/form/NumberInput';
import '../../shared/ReactSelectDarkMode.css';
import { api } from '../../shared/api';
import { CenteredLoader } from '../../components/LoadingSpinner';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { useConfirm } from '../../shared/ConfirmContext';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const BundleDetail = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { bundleId } = useParams();

  const queryClient = useQueryClient();

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Add-entry form
  const [itemOptions, setItemOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [entryQty, setEntryQty] = useState(1);
  const [entryNotes, setEntryNotes] = useState('');

  const { data: bundle, isLoading: loading } = useQuery({
    queryKey: ['bundle-detail', bundleId],
    queryFn: () => api.get(`warehouses/api/item-bundles/${bundleId}/`),
    onError: () => setError('Error loading bundle.'),
  });

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.itembundle' || e.model === 'warehouses.itembundleentry') {
      queryClient.invalidateQueries({ queryKey: ['bundle-detail', bundleId] });
    }
  });

  const addEntryMutation = useMutation({
    mutationFn: () => api.post(`warehouses/api/item-bundles/${bundleId}/entries/`, {
      stock_item: selectedItem.value, quantity: entryQty, notes: entryNotes,
    }),
    onSuccess: () => {
      setSelectedItem(null); setEntryQty(1); setEntryNotes('');
      setSuccess(t('warehouses.bundles.addSuccess'));
      setTimeout(() => setSuccess(null), 3000);
      queryClient.invalidateQueries({ queryKey: ['bundle-detail', bundleId] });
    },
    onError: () => {
      setError(t('warehouses.bundles.addError'));
      setTimeout(() => setError(null), 5000);
    },
  });
  const addingEntry = addEntryMutation.isPending;

  const removeEntryMutation = useMutation({
    mutationFn: (entryId) => api.delete(`warehouses/api/item-bundles/${bundleId}/entries/${entryId}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bundle-detail', bundleId] }),
    onError: () => {
      setError(t('warehouses.bundles.addError'));
      setTimeout(() => setError(null), 5000);
    },
  });

  const handleItemSearch = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 2) return;
    const search = async () => {
      try {
        const response = await api.get(
          `warehouses/api/stock-items/?search=${encodeURIComponent(inputValue)}&page_size=50`
        );
        const items = Array.isArray(response) ? response : (response.results || []);
        setItemOptions(items.map(item => ({
          value: item.id,
          label: `${item.product_name} (${item.stock_name || item.stock}) — ${t('warehouses.bundles.entryCount')}: ${item.quantity_available ?? '?'}`,
          item,
        })));
      } catch (err) {
        console.error('Item search error:', err);
        setError(String(err.message || err));
        setTimeout(() => setError(null), 5000);
      }
    };
    search();
  }, [t]);

  const handleAddEntry = () => {
    if (!selectedItem) return;
    addEntryMutation.mutate();
  };

  const handleRemoveEntry = async (entryId) => {
    if (!await confirm(t('warehouses.bundles.removeItem') + '?')) return;
    removeEntryMutation.mutate(entryId);
  };

  if (loading) {
    return (
      <PageShell
        pretitle={t('warehouses.bundles.detail')}
        title={t('warehouses.bundles.title', 'Bundles')}
        description={t('warehouses.bundles.subtitle', 'Review bundle composition and maintain entry structure in a shared detail layout.')}
      >
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  if (!bundle) {
    return (
      <PageShell
        pretitle={t('warehouses.bundles.detail')}
        title={t('warehouses.bundles.title', 'Bundles')}
        description={t('warehouses.bundles.subtitle', 'Review bundle composition and maintain entry structure in a shared detail layout.')}
      >
        <PageSection>
          <EmptyState
            icon={<i className="ti ti-package app-empty-state__icon-glyph" aria-hidden="true" />}
            title={t('warehouses.bundles.notFound', 'Bundle not found')}
            message={error || t('warehouses.bundles.notFoundMessage', 'The requested bundle could not be loaded.')}
            action={<Link to="/warehouses/bundles" className="btn btn-primary">{t('common.back')}</Link>}
          />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('warehouses.bundles.detail')}
      title={bundle.name}
      description={t('warehouses.bundles.subtitle', 'Review bundle composition and maintain entry structure in a shared detail layout.')}
      actions={<Link to="/warehouses/bundles" className="btn btn-outline-secondary"><i className="ti ti-arrow-left me-2"></i>{t('common.back')}</Link>}
    >
      {error && (
        <div className="alert alert-danger app-inline-alert" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success app-inline-alert" role="alert">
          {success}
        </div>
      )}

      <PageSection
        title={t('warehouses.items.detail')}
        description={t('warehouses.bundles.detailsDescription', 'Primary bundle metadata, identifiers and operational notes.')}
      >
        <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">{t('warehouses.bundles.name')}</div>
                      <div className="datagrid-content">{bundle.name}</div>
                    </div>
                    {bundle.qr_code && (
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('warehouses.bundles.qrCode')}</div>
                        <div className="datagrid-content"><code>{bundle.qr_code}</code></div>
                      </div>
                    )}
                    {bundle.description && (
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('warehouses.bundles.description')}</div>
                        <div className="datagrid-content">{bundle.description}</div>
                      </div>
                    )}
                    {bundle.notes && (
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('warehouses.bundles.notes')}</div>
                        <div className="datagrid-content text-muted">{bundle.notes}</div>
                      </div>
                    )}
        </div>
      </PageSection>

      <PageSection
        title={<>{t('warehouses.bundles.entries')}<span className="badge bg-secondary ms-2">{bundle.entries.length}</span></>}
        description={t('warehouses.bundles.entriesDescription', 'Bundle composition and quantity mapping to stock items.')}
        bodyClassName="p-0"
      >
        {bundle.entries.length === 0 ? (
          <EmptyState
            className="py-5"
            icon={<i className="ti ti-package-import app-empty-state__icon-glyph" aria-hidden="true" />}
            title={t('warehouses.bundles.noEntries')}
            message={t('warehouses.bundles.noEntriesMessage', 'Start by adding the first stock item into this bundle.')}
          />
        ) : (
          <div className="table-responsive">
            <table className="table table-vcenter card-table app-table mb-0">
                    <thead>
                      <tr>
                        <th>{t('warehouses.bundles.item')}</th>
                        <th>{t('warehouses.bundles.quantity')}</th>
                        <th>{t('warehouses.bundles.entryNotes')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bundle.entries.map(entry => (
                        <tr key={entry.id}>
                          <td>
                            <span className="fw-semibold">{entry.item_name}</span>
                            {entry.item_stock_name && (
                              <span className="text-muted small ms-1">({entry.item_stock_name})</span>
                            )}
                          </td>
                          <td>{entry.quantity}</td>
                          <td className="text-muted">{entry.notes}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-ghost-danger btn-sm"
                              onClick={() => handleRemoveEntry(entry.id)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M4 7l16 0"></path>
                                <path d="M10 11l0 6"></path>
                                <path d="M14 11l0 6"></path>
                                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
            </table>
          </div>
        )}

        <div className="card-body border-top">
          <div className="row g-2 align-items-end">
                    <div className="col-md-5">
                      <label className="form-label">{t('warehouses.bundles.addItem')}</label>
                      <Select
                        options={itemOptions}
                        value={selectedItem}
                        onChange={setSelectedItem}
                        onInputChange={handleItemSearch}
                        isClearable
                        placeholder={t('warehouses.bundles.searchItem')}
                        filterOption={() => true}
                        classNamePrefix="react-select"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">{t('warehouses.bundles.quantity')}</label>
                      <NumberInput
                        min={1}
                        step="any"
                        value={entryQty}
                        onChange={setEntryQty}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('warehouses.bundles.entryNotes')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={entryNotes}
                        onChange={e => setEntryNotes(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        className="btn btn-primary w-100"
                        onClick={handleAddEntry}
                        disabled={!selectedItem || addingEntry}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M12 5l0 14"></path>
                          <path d="M5 12l14 0"></path>
                        </svg>
                        {t('warehouses.bundles.addItem')}
                      </button>
                    </div>
          </div>
        </div>
      </PageSection>
    </PageShell>
  );
};

export default BundleDetail;
